import { useEffect, useState } from 'react';
import Link from 'next/link';
import SafeImage from '@ui/SafeImage';
import Button from '@ui/Button';
import { defaultItems as categoryChips } from '@c/marketplace/CategoryNav';
import { searchAlgolia } from '@util/firestore/algolia/search.service';
import { MasterDocument } from '@util/firestore/master';
import { pushToDataLayer } from '@util/analytics';
import NotifyMeModule from '@c/master/NotifyMeModule';

// Matches the design doc's D1 spec (hitsPerPage: 2) and the exact call shape
// SearchBox/SearchBar already use for the GearMaster (GM) app — same client,
// same index, same "GM" app key. Only the top hit is rendered as a card, but
// the request stays hitsPerPage: 2 to match the approved spec/call shape.
const GM_HITS_PER_PAGE = 2;

// GB #1401 final-review-round fix (required): tracks which keywords have
// already fired `search_zero_results` during this page session. Module
// state, NOT component state — this component unmounts and remounts
// whenever PageMainContent's `isFetching` flag toggles true then false
// (sort change, page-size change, applying then clearing a filter), even
// when the keyword hasn't changed and the search is still genuinely
// zero-result. A component-scoped guard (e.g. a ref or the effect's own
// dependency array) resets on every one of those remounts and re-fires the
// event for the same keyword, inflating the metric this whole feature is
// measured by. This Set survives that unmount/remount cycle, while still
// resetting naturally on a real full page load (fresh JS module instance).
// No eviction logic: the number of distinct keywords one visitor searches
// in a single browsing session is small enough that this is not a memory
// concern.
const firedZeroResultKeywords = new Set<string>();

/** Test-only: reset the fired-keyword set between cases. */
export function __resetFiredZeroResultKeywordsForTests(): void {
  firedZeroResultKeywords.clear();
}

// Common English stopwords that shouldn't count as a "meaningful" token when
// checking keyword/hit relevance below — a shared word like "with" or "for"
// overlapping is not evidence the hit actually relates to the query.
const STOPWORDS = new Set([
  'the', 'and', 'for', 'with', 'from', 'this', 'that', 'used', 'new',
  'camera', 'cameras', 'lens', 'lenses', 'gear', 'kit',
]);

/**
 * Lightweight relevance guard (GB #1401 gate-review fix, MEDIUM): Algolia's
 * typo tolerance means a generic or unrelated query ("tripod", "camera bag")
 * can surface an arbitrary specific model as modelHits[0]. Without a floor,
 * SearchEmptyState would then offer a notify-me signup — a specific,
 * concrete promise tied to a real backend write — for gear the buyer never
 * asked about.
 *
 * Heuristic, not a ranking system: true if the lowercased/trimmed keyword
 * shares at least one meaningful token with the hit's title, brand, or
 * model. A token counts as meaningful if it's 3+ characters (and not a
 * stopword), OR — Codex review fix (GB #1401, P2) — if it's 2+ characters
 * AND contains at least one digit. Without the digit carve-out, an exact
 * search for a short model code (`Z9`, `A7`, `R5`, `X1`, `Q3`, all 2
 * characters) had every one of its tokens dropped by the length-3 floor, so
 * isRelevantMatch always returned false even when Algolia's top hit WAS the
 * exact requested model — hiding the guide/notify-me offer for exactly the
 * precise, high-intent searches this feature should serve best. Plain short
 * English words ("of", "in", "to", "on") contain no digit, so they still
 * fail this test and remain filtered as before.
 */
export function isRelevantMatch(
  keyword: string,
  hit: { title?: string | null; brand?: string | null; model?: string | null }
): boolean {
  const keywordTokens = keyword
    .toLowerCase()
    .trim()
    .split(/[^a-z0-9]+/)
    .filter(
      (token) =>
        (token.length >= 3 || (token.length >= 2 && /\d/.test(token))) &&
        !STOPWORDS.has(token)
    );

  if (keywordTokens.length === 0) return false;

  const haystack = [hit.title, hit.brand, hit.model]
    .filter((value): value is string => Boolean(value))
    .join(' ')
    .toLowerCase();

  if (!haystack) return false;

  return keywordTokens.some((token) => haystack.includes(token));
}

/**
 * Zero-result empty state for the search results page (GB #1401, D1).
 *
 * Renders ONLY static/deterministic content — no ListProductCard/
 * MarketplaceProductCard, no ProductSchema/Offer markup, no moment()/
 * Date.now()/toLocaleString() at render time. The search page already has a
 * separate hydration-error problem (date rendering on product cards); this
 * component must not add to it, and it never renders anything schema.org
 * "product" shaped for a page that has no real product — that's the /m/
 * page hard rule (MasterSEO.tsx:77 forbids Offer schema on model pages; this
 * only LINKS to /m/ pages, it never re-implements their markup here).
 */
const SearchEmptyState = ({ keyword }: { keyword: string }) => {
  const [modelHits, setModelHits] = useState<MasterDocument[]>([]);
  const [modelSearchSettled, setModelSearchSettled] = useState(false);

  // Client-side GearMaster lookup for the raw keyword — the generic routing
  // mechanism from the design doc: no GM hit, no model card, just the
  // category/browse fallback below.
  useEffect(() => {
    let cancelled = false;
    setModelSearchSettled(false);
    // GB #1401 gate-review fix (MEDIUM): without this, the PREVIOUS
    // keyword's matched model card (and its notify-me widget) stayed
    // visible during the new keyword's in-flight lookup, so a click in that
    // window would attribute to the wrong model.
    setModelHits([]);

    searchAlgolia('GM', 'products', {
      query: keyword,
      page: 0,
      hitsPerPage: GM_HITS_PER_PAGE,
    }).then((result) => {
      if (cancelled) return;
      // GB #1401 gate-review fix (LOW): `error: true` distinguishes "the
      // GearMaster search itself failed" (e.g. missing/misconfigured search
      // key) from "genuinely zero matches". The rendered UI is unchanged
      // either way (category fallback is still the right degraded UX) —
      // this only makes the failure observable in analytics instead of
      // silently and permanently looking like zero matches.
      if (result.error) {
        pushToDataLayer('zero_state_model_lookup_error', { query: keyword });
      }
      setModelHits((result.data || []) as MasterDocument[]);
      setModelSearchSettled(true);
    });

    return () => {
      cancelled = true;
    };
  }, [keyword]);

  // search_zero_results fires once per keyword PER PAGE SESSION — the effect
  // dependency array alone only prevents re-firing on a re-render of THIS
  // component instance; it does nothing across an unmount/remount (which
  // PageMainContent triggers on every isFetching toggle: sort change,
  // page-size change, applying then clearing a filter). The
  // firedZeroResultKeywords module-level Set (declared above) is what
  // actually enforces "once per keyword" across those remounts.
  useEffect(() => {
    if (firedZeroResultKeywords.has(keyword)) return;
    firedZeroResultKeywords.add(keyword);
    pushToDataLayer('search_zero_results', { query: keyword });
  }, [keyword]);

  const rawBestMatch = modelHits[0] as
    | (MasterDocument & { objectID?: string })
    | undefined;
  // Algolia hits are always guaranteed an objectID; MasterDocument.id is not
  // guaranteed to be a separate indexed field (SearchBox/SearchBar both fall
  // back to objectID for their React `key`s for the same reason). NotifyMeModule
  // and /api/master/watch need a real masterId, so normalize it here rather
  // than risk silently posting an empty masterId.
  const normalizedBestMatch = rawBestMatch
    ? { ...rawBestMatch, id: rawBestMatch.id ?? rawBestMatch.objectID ?? '' }
    : undefined;
  // GB #1401 gate-review fix (MEDIUM): a generic/unrelated query can surface
  // an arbitrary specific model via Algolia typo tolerance. Don't treat it
  // as "the" match unless the keyword actually shares a meaningful token
  // with it — otherwise this renders a false, specific notify-me promise
  // for gear the buyer never asked about.
  const bestMatch =
    normalizedBestMatch && isRelevantMatch(keyword, normalizedBestMatch)
      ? normalizedBestMatch
      : undefined;
  const bestMatchThumbnail = bestMatch?.thumbnail ?? bestMatch?.images?.[0];
  const sellSubject = bestMatch?.title ?? keyword;

  return (
    <div className="order-1 flex w-full grow flex-col items-center gap-[2.4rem] self-stretch py-[4rem]">
      <div className="flex max-w-[52rem] flex-col items-center gap-[0.8rem] px-[2rem] text-center">
        <h2 className="text-[2rem] font-semibold sm:text-[2.4rem]">
          No listings for &quot;{keyword}&quot; right now
        </h2>
        <p className="text-brand-dark-gray">
          New gear is listed daily &mdash; here&apos;s what you can do:
        </p>
      </div>

      {bestMatch && bestMatch.id && bestMatch.slug && bestMatch.title ? (
        <div className="flex w-full max-w-[44rem] flex-col items-center gap-[1.6rem] px-[2rem]">
          <Link
            href={`/m/${bestMatch.slug}`}
            onClick={() =>
              pushToDataLayer('zero_state_model_click', {
                query: keyword,
                slug: bestMatch.slug,
              })
            }
            className="flex w-full items-center gap-[1.6rem] rounded-[1rem] border-[1px] border-brand-light-gray p-[1.6rem] transition-colors hover:bg-brand-lightest-gray"
          >
            {bestMatchThumbnail && (
              <SafeImage
                src={bestMatchThumbnail}
                alt={bestMatch.title}
                height={64}
                width={64}
                style={{ objectFit: 'cover' }}
                className="size-[6.4rem] shrink-0 rounded-[0.8rem]"
              />
            )}
            <span className="text-[1.5rem]">
              View the {bestMatch.title} guide &mdash; specs, price history,
              and current market value.
            </span>
          </Link>

          <div className="flex w-full flex-col items-center gap-[1.2rem] text-center">
            <p className="text-[1.4rem] font-medium">
              Get an email when a {bestMatch.title} is listed.
            </p>
            <NotifyMeModule
              key={bestMatch.id}
              master={bestMatch}
              onSuccess={() =>
                pushToDataLayer('zero_state_notify_submit', {
                  query: keyword,
                  masterId: bestMatch.id,
                })
              }
            />
          </div>
        </div>
      ) : modelSearchSettled ? (
        <div className="flex w-full max-w-[56rem] flex-col items-center gap-[2rem] px-[2rem]">
          <div className="flex flex-wrap justify-center gap-[0.8rem]">
            {categoryChips.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() =>
                  pushToDataLayer('zero_state_category_click', {
                    query: keyword,
                    category: item.label,
                  })
                }
                className="rounded-full border-[1px] border-brand-light-gray px-[1.6rem] py-[0.8rem] text-[1.4rem] transition-colors hover:bg-brand-lightest-gray"
              >
                {item.label}
              </Link>
            ))}
          </div>

          <Link
            href="/collections/new-arrivals"
            className="text-[1.4rem] font-semibold text-brand-secondary hover:underline"
          >
            Browse newest listings
          </Link>

          <p className="text-[1.3rem] text-brand-dark-gray">
            Try fewer words or check your spelling.
          </p>
        </div>
      ) : null}

      <div className="px-[2rem] text-center">
        <Button
          text={`Have a ${sellSubject} to sell? List it free — buyers are searching for it.`}
          type="tertiary"
          href="/sell/create-listing"
          className="!h-auto !whitespace-normal !py-[1.2rem] !text-left !leading-snug"
          onClick={() => pushToDataLayer('zero_state_sell_click', { query: keyword })}
        />
      </div>
    </div>
  );
};

export default SearchEmptyState;
